import React from "react";
import { Drawer, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import {
  BORDA_DESTAQUE,
  FONTE,
  FUNDO_BLOCO,
  FUNDO_DESTAQUE,
  NAVY,
  TEXTO_TABELA as ROTULO,
  VERMELHO_TEXTO,
} from "../estilos/tokens";
import { percentual, reais as moeda } from "../formatadores";
import { ArquivoRelacionado, buscarRastreabilidade, TipoTransacao } from "../../../../services/home";
import { useRequisicao } from "../hooks/useRequisicao";

interface Props {
  codLinha: number | null;
  origem?: string;
  tipo: TipoTransacao;
  aberto: boolean;
  aoFechar: () => void;
}

const cartao: React.CSSProperties = {
  background: FUNDO_BLOCO,
  borderRadius: 16,
  padding: 20,
  marginTop: 20,
};

const cartaoDestaque: React.CSSProperties = {
  ...cartao,
  background: FUNDO_DESTAQUE,
  border: `2px solid ${BORDA_DESTAQUE}`,
};

const cartaoTitulo: React.CSSProperties = {
  fontFamily: FONTE,
  fontSize: 15,
  fontWeight: 700,
  color: NAVY,
  margin: "0 0 16px",
};

const rotuloCampo: React.CSSProperties = {
  fontFamily: FONTE,
  fontSize: 13,
  fontWeight: 400,
  color: ROTULO,
};

const valorCampo: React.CSSProperties = {
  fontFamily: FONTE,
  fontSize: 15,
  fontWeight: 700,
  color: NAVY,
  marginTop: 4,
  wordBreak: "break-word",
};

const celulaTabela: React.CSSProperties = {
  padding: "8px 8px",
  fontFamily: FONTE,
  fontSize: 13,
  textAlign: "center",
  color: NAVY,
  fontWeight: 700,
  whiteSpace: "nowrap",
  background: "transparent",
};

const cabecalhoTabela: React.CSSProperties = {
  ...celulaTabela,
  fontWeight: 400,
  color: ROTULO,
  paddingBottom: 12,
  border: "none",
};

const celulaArquivo: React.CSSProperties = {
  ...celulaTabela,
  textAlign: "left",
  whiteSpace: "normal",
  wordBreak: "break-all",
};

const textoVazio: React.CSSProperties = {
  fontFamily: FONTE,
  fontSize: 13,
  color: ROTULO,
  margin: 0,
};

const secaoTitulo: React.CSSProperties = {
  fontFamily: FONTE,
  fontSize: 18,
  fontWeight: 700,
  color: NAVY,
  margin: "28px 0 0",
};

const linhaCabecalho: React.CSSProperties = { background: "transparent" };

const grade: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "16px",
};

const Campo = ({ nome, texto }: { nome: string; texto: string }) => (
  <div>
    <div style={rotuloCampo}>{nome}</div>
    <div style={valorCampo}>{texto || "-"}</div>
  </div>
);

const TabelaArquivos = ({ titulo, arquivos = [] }: { titulo: string; arquivos?: ArquivoRelacionado[] }) => (
  <div style={cartao}>
    <h3 style={cartaoTitulo}>{titulo}</h3>
    {arquivos.length === 0 ? (
      <p style={textoVazio}>Sem arquivos para exibir.</p>
    ) : (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={linhaCabecalho}>
              <th style={{ ...cabecalhoTabela, textAlign: "left" }}>Nome do arquivo</th>
              <th style={cabecalhoTabela}>Data e hora de recebimento</th>
            </tr>
          </thead>
          <tbody>
            {arquivos.map((arquivo, indice) => (
              <tr key={`${arquivo.nome}-${indice}`}>
                <td style={celulaArquivo}>{arquivo.nome}</td>
                <td style={celulaTabela}>{arquivo.recebimento || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const RastreabilidadeTransacao = ({ codLinha, origem, tipo, aberto, aoFechar }: Props) => {
  const { dados, carregando, erro } = useRequisicao(
    () => buscarRastreabilidade(codLinha as number, tipo, origem),
    [codLinha, tipo, origem],
    codLinha != null
  );

  const pagamento = tipo === "PAGAMENTO";
  const antecipacao = tipo === "ANTECIPADO";
  const titulo = antecipacao
    ? "Rastreabilidade da antecipação"
    : pagamento
    ? "Rastreabilidade do pagamento"
    : "Rastreabilidade da transação";

  return (
    <Drawer anchor="right" open={aberto} onClose={aoFechar} ModalProps={{ disableScrollLock: true }}>
      <div style={{ width: 600, maxWidth: "92vw", padding: 24, boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2
            style={{
              fontFamily: FONTE,
              fontSize: 20,
              fontWeight: 700,
              color: NAVY,
              margin: 0,
            }}
          >
            {titulo}
          </h2>
          <IconButton size="small" onClick={aoFechar} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </div>

        {carregando && <p style={{ ...textoVazio, marginTop: 32 }}>Carregando...</p>}

        {!carregando && erro && (
          <p style={{ ...textoVazio, color: VERMELHO_TEXTO, marginTop: 32 }}>{erro}</p>
        )}

        {!carregando && !erro && dados && !pagamento && !antecipacao && (
          <>
            <div style={cartao}>
              <h3 style={cartaoTitulo}>Informações do arquivo</h3>
              <Campo nome="Nome do arquivo" texto={dados.nomeArquivo} />
              <div style={{ marginTop: 16 }}>
                <Campo nome="Origem e Recebimento" texto={dados.origem} />
              </div>
              <div style={{ ...grade, marginTop: 16 }}>
                <Campo nome="NSU" texto={dados.nsu} />
                <Campo nome="AUT" texto={dados.autorizacao} />
                <Campo nome="RO" texto={dados.ro} />
              </div>
            </div>

            <h3 style={secaoTitulo}>
              {dados.parcelas.length === 1 ? "Detalhamento da parcela" : "Detalhamento das parcelas"}
            </h3>
            <div style={{ ...cartao, marginTop: 12 }}>
              {dados.parcelas.length === 0 ? (
                <p style={textoVazio}>Sem parcelas para exibir.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={linhaCabecalho}>
                        <th style={cabecalhoTabela}>Parcela</th>
                        <th style={cabecalhoTabela}>Vencimento</th>
                        <th style={cabecalhoTabela}>Valor bruto</th>
                        <th style={cabecalhoTabela}>Taxa</th>
                        <th style={cabecalhoTabela}>Valor líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.parcelas.map((p, indice) => (
                        <tr key={`${p.parcela}-${indice}`}>
                          <td style={celulaTabela}>{p.parcela}</td>
                          <td style={celulaTabela}>{p.vencimento}</td>
                          <td style={celulaTabela}>{moeda(p.valorBruto)}</td>
                          <td style={celulaTabela}>{percentual(p.taxaPercentual)}</td>
                          <td style={celulaTabela}>{moeda(p.valorLiquido)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {!carregando && !erro && dados && pagamento && (
          <>
            <div style={cartaoDestaque}>
              <h3 style={cartaoTitulo}>Informações do pagamento</h3>
              <div style={grade}>
                <Campo nome="NSU" texto={dados.nsu} />
                <Campo nome="AUT" texto={dados.autorizacao} />
                <Campo nome="RO" texto={dados.ro} />
                <Campo nome="TID" texto={dados.tid} />
                <Campo nome="Data e hora" texto={dados.dataHora} />
                <Campo nome="Operadora" texto={dados.operadora} />
                <Campo nome="Valor bruto" texto={moeda(dados.valorBruto)} />
                <Campo nome="Valor líquido" texto={moeda(dados.valorLiquido)} />
                <Campo nome="Status" texto={dados.status} />
              </div>
            </div>

            <TabelaArquivos titulo="Arquivos relacionados ao pagamento" arquivos={dados.arquivos} />

            <div style={cartao}>
              <h3 style={cartaoTitulo}>Parcelas quitadas até o momento</h3>
              {dados.parcelas.length === 0 ? (
                <p style={textoVazio}>Sem parcelas para exibir.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={linhaCabecalho}>
                        <th style={cabecalhoTabela}>Parcela</th>
                        <th style={cabecalhoTabela}>Vencimento</th>
                        <th style={cabecalhoTabela}>Valor bruto</th>
                        <th style={cabecalhoTabela}>Valor líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.parcelas.map((p, indice) => (
                        <tr key={`${p.parcela}-${indice}`}>
                          <td style={celulaTabela}>{p.parcela}</td>
                          <td style={celulaTabela}>{p.vencimento}</td>
                          <td style={celulaTabela}>{moeda(p.valorBruto)}</td>
                          <td style={celulaTabela}>{moeda(p.valorLiquido)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {!carregando && !erro && dados && antecipacao && (
          <>
            <div style={cartaoDestaque}>
              <h3 style={cartaoTitulo}>Informações do pagamento</h3>
              <div style={grade}>
                <Campo nome="NSU" texto={dados.nsu} />
                <Campo nome="AUT" texto={dados.autorizacao} />
                <Campo nome="RO" texto={dados.ro} />
                <Campo nome="TID" texto={dados.tid} />
                <Campo nome="Data e hora" texto={dados.dataHora} />
                <Campo nome="Operadora" texto={dados.operadora} />
                <Campo nome="Valor bruto" texto={moeda(dados.valorBruto)} />
                <Campo nome="Taxa antecipação" texto={percentual(dados.taxaAntecipacao)} />
                <Campo nome="Taxa MDR" texto={percentual(dados.taxaMdr)} />
                <Campo nome="Valor líquido" texto={moeda(dados.valorLiquido)} />
              </div>
            </div>

            <TabelaArquivos titulo="Arquivos relacionados ao pagamento" arquivos={dados.arquivos} />

            <TabelaArquivos
              titulo="Arquivos relacionados à antecipação"
              arquivos={dados.arquivosAntecipacao || []}
            />

            <div style={cartao}>
              <h3 style={cartaoTitulo}>Parcelas quitadas até o momento</h3>
              {dados.parcelas.length === 0 ? (
                <p style={textoVazio}>Sem parcelas para exibir.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={linhaCabecalho}>
                        <th style={cabecalhoTabela}>Parcela</th>
                        <th style={cabecalhoTabela}>Vencimento</th>
                        <th style={cabecalhoTabela}>Valor bruto</th>
                        <th style={cabecalhoTabela}>Valor líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.parcelas.map((p, indice) => (
                        <tr key={`${p.parcela}-${indice}`}>
                          <td style={celulaTabela}>{p.parcela}</td>
                          <td style={celulaTabela}>{p.vencimento}</td>
                          <td style={celulaTabela}>{moeda(p.valorBruto)}</td>
                          <td style={celulaTabela}>{moeda(p.valorLiquido)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default RastreabilidadeTransacao;
